import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, navigate, useRoute } from './router';
import { store, useAppState, useNow, useReducedMotion, useStudio } from './hooks';
import { Icon } from '@/ui/Icon';
import { LevelHex, Meter, Sys } from '@/ui/hud';
import { levelInfo } from '@/engine/progression';
import { searchCatalog } from '@/engine/search';
import { levelUnlocks } from '@/data/progression';
import { viewsOf } from '@/engine/views';
import { relTime } from '@/core/util/misc';
import type { StudioEvent } from '@/core/events';
import { catalog } from '@/data/catalog';
import { L, LANGS, LL, type Lang } from '@/i18n';
import { ComboPop, systemNotify, TypeLines } from '@/ui/system';
import { play } from '@/ui/sound';
import { onToast } from '@/ui/toast';
import { onAiFallback } from '@/ai/providers';
import { burst, BurstLayer } from '@/ui/burst';
import { useAccount } from '@/cloud/account';
import { syncLabel } from '@/features/account/Account';

type Badge = 'mistakes' | 'reviews' | 'quests';
interface NavItem { to: string; label: string; icon: string; badge?: Badge; feature?: boolean }
interface NavGroup { group: string; items: NavItem[]; collapsed?: boolean }

/** One navigation for every grade — the original layout, plus the new Assessment tab. */
function navGroups(): NavGroup[] {
  return [
    { group: L('Study', 'Studer'), items: [
      { to: '/today', label: L('Today', 'I dag'), icon: 'today' },
      { to: '/learn', label: L('Learn', 'Lær'), icon: 'learn' },
      { to: '/assess', label: L('Assessment', 'Vurdering'), icon: 'assess', feature: true },
      { to: '/courses', label: L('Courses', 'Kurs'), icon: 'courses' },
      { to: '/subjects', label: L('Subjects', 'Fag'), icon: 'subjects' },
      { to: '/map', label: L('Knowledge map', 'Kunnskapskart'), icon: 'map' },
    ] },
    { group: L('Plan', 'Plan'), items: [
      { to: '/planner', label: L('Planner', 'Planlegger'), icon: 'planner' },
      { to: '/exams', label: L('Exams', 'Prøver'), icon: 'exams' },
      { to: '/goals', label: L('Goals', 'Mål'), icon: 'goals' },
    ] },
    { group: L('Progress', 'Fremgang'), items: [
      { to: '/mastery', label: L('Mastery', 'Mestring'), icon: 'mastery' },
      { to: '/analytics', label: L('Analytics', 'Analyse'), icon: 'analytics' },
      { to: '/mistakes', label: L('Mistakes', 'Feilbank'), icon: 'mistakes', badge: 'mistakes' },
      { to: '/reviews', label: L('Reviews', 'Repetisjon'), icon: 'reviews', badge: 'reviews' },
      { to: '/profile', label: L('Learning profile', 'Læringsprofil'), icon: 'profile' },
    ] },
    { group: L('System', 'System'), items: [
      { to: '/quests', label: L('Quests', 'Oppdrag'), icon: 'quests', badge: 'quests' },
      { to: '/achievements', label: L('Achievements', 'Prestasjoner'), icon: 'achievements' },
      { to: '/notes', label: L('Notes', 'Notater'), icon: 'notes' },
      { to: '/palace', label: L('Memory palace', 'Minnepalass'), icon: 'palace' },
      { to: '/account', label: L('Account', 'Konto'), icon: 'shield' },
      { to: '/settings', label: L('Settings', 'Innstillinger'), icon: 'settings' },
    ] },
    { group: L('Tools', 'Verktøy'), items: [
      { to: '/calibration', label: LL('Level check', 'Nivåsjekk', { detailed: ['Calibration', 'Kartlegging'] }), icon: 'target' },
      { to: '/feynman', label: LL('Explain it (Feynman)', 'Forklar det (Feynman)', { detailed: ['Feynman mode', 'Feynman-modus'] }), icon: 'brain' },
      { to: '/writing', label: L('Writing coach', 'Skrivecoach'), icon: 'pen' },
      { to: '/chat', label: L('Study chat', 'Studiechat'), icon: 'speech', feature: true },
    ] },
  ];
}

export function BrandMark({ size = 34 }: { size?: number }) {
  return (
    <svg className="brand-mark" viewBox="0 0 40 40" width={size} height={size} aria-hidden="true">
      <defs>
        <linearGradient id="bm" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="var(--accent)" />
          <stop offset="1" stopColor="var(--accent-2)" />
        </linearGradient>
      </defs>
      <path d="M20 2 36 11v18L20 38 4 29V11Z" fill="none" stroke="url(#bm)" strokeWidth="2" />
      <path d="M20 9 30 14.5v11L20 31 10 25.5v-11Z" fill="rgba(var(--accent-rgb),0.08)" stroke="var(--accent-2)" strokeOpacity="0.6" />
      <circle cx="20" cy="20" r="3.4" fill="var(--accent)" />
      <path d="M20 2v7M36 11l-6 3.5M36 29l-6-3.5M20 38v-7M4 29l6-3.5M4 11l6 3.5" stroke="var(--accent)" strokeOpacity="0.5" />
    </svg>
  );
}

function useBadges() {
  const state = useAppState();
  const now = useNow();
  return useMemo(() => {
    const views = viewsOf(state, now);
    const due = Object.values(views).filter((v) => v.evidenceCount > 0 && v.due !== null && v.due <= now && v.relevance !== 'unavailable').length + state.flashcards.filter((c) => !c.memory.due || c.memory.due <= now).length;
    return {
      mistakes: state.mistakes.filter((m) => m.status !== 'resolved').length,
      reviews: due,
      quests: state.quests.filter((q) => !q.completedAt && (!q.expiresAt || q.expiresAt > now)).length,
    };
  }, [state, now]);
}

/** Where this profile lives: demo, this device only, or synced to an account. */
function RailFoot({ isDemo, onNavigate }: { isDemo: boolean; onNavigate: () => void }) {
  const account = useAccount();
  if (account.phase === 'signed-in') {
    return <Link to="/account" onClick={onNavigate} style={{ color: 'inherit' }}><span className={`sync-dot ${account.sync}`} />@{account.user?.username} · {syncLabel(account)}</Link>;
  }
  if (isDemo) return <>{L('DEMO PROFILE · simulated history', 'DEMOPROFIL · simulert historikk')}</>;
  return (
    <>
      {LL('GUEST · saved on this device', 'GJEST · lagret på denne enheten', { detailed: ['LOCAL PROFILE · stored on this device', 'LOKAL PROFIL · lagret på denne enheten'] })}
      {account.phase === 'guest' && <> · <Link to="/account" onClick={onNavigate}>{LL('Log in to save online', 'Logg inn for å lagre på nett', { detailed: ['Log in to sync', 'Logg inn for å synkronisere'] })}</Link></>}
    </>
  );
}

function AccountButton() {
  const account = useAccount();
  const signedIn = account.phase === 'signed-in';
  return (
    <Link to="/account" className="icon-btn account-btn" aria-label={signedIn ? L(`Account: ${account.user?.displayName}`, `Konto: ${account.user?.displayName}`) : L('Log in or create an account', 'Logg inn eller opprett konto')} title={signedIn ? `@${account.user?.username} · ${syncLabel(account)}` : L('Log in', 'Logg inn')}>
      <Icon name="profile" />
      {signedIn && <span className="dot" />}
    </Link>
  );
}

function Rail({ open, onClose }: { open: boolean; onClose: () => void }) {
  const route = useRoute();
  const badges = useBadges();
  const profile = useStudio((s) => s.profile);
  const groups = navGroups();
  const item = (it: NavItem) => {
    const active = route.path === it.to || route.path.startsWith(`${it.to}/`);
    const count = it.badge ? badges[it.badge] : 0;
    return (
      <Link key={it.to} to={it.to} className={`nav-item ${active ? 'active' : ''} ${it.feature && !active ? 'feature' : ''}`} aria-current={active ? 'page' : undefined} onClick={onClose}>
        <Icon name={it.icon} />
        {it.label}
        {count > 0 ? <span className={`count ${it.badge === 'mistakes' ? '' : 'cool'}`}>{count}</span> : it.feature ? <span className="new">{L('NEW', 'NY')}</span> : null}
      </Link>
    );
  };
  return (
    <>
      {open && <div className="rail-scrim" onClick={onClose} />}
      <nav className={`rail ${open ? 'open' : ''}`} aria-label={L('Main navigation', 'Hovedmeny')}>
        <Link to="/today" className="brand" onClick={onClose}>
          <BrandMark />
          <span>
            <div className="brand-name">STUDIO</div>
            <div className="brand-sub">{LL('Learning adventure · v0.3', 'Læringseventyr · v0.3', { detailed: ['Learning OS · v0.2', 'Læringssystem · v0.3'] })}</div>
          </span>
        </Link>
        {groups.map((g) =>
          g.collapsed ? (
            <details className="nav-group collapsible" key={g.group} open={g.items.some((it) => route.path.startsWith(it.to))}>
              <summary className="nav-group-label">{g.group} <Icon name="chevron-down" size={12} /></summary>
              {g.items.map(item)}
            </details>
          ) : (
            <div className="nav-group" key={g.group}>
              <div className="nav-group-label">{g.group}</div>
              {g.items.map(item)}
            </div>
          ),
        )}
        <div className="rail-foot"><RailFoot isDemo={!!profile?.isDemo} onNavigate={onClose} /></div>
      </nav>
    </>
  );
}

function XpWidget() {
  const total = useStudio((s) => s.xp.total);
  const info = levelInfo(total);
  // The XP bar flashes and the level hexagon bounces whenever XP is earned.
  const [pulse, setPulse] = useState(0);
  const prev = useRef(total);
  useEffect(() => {
    if (total > prev.current) setPulse((p) => p + 1);
    prev.current = total;
  }, [total]);
  return (
    <Link key={pulse} to="/quests" className={`xpbar ${pulse ? 'pulse' : ''}`} aria-label={L(`Level ${info.level}, ${info.into} of ${info.needed} XP`, `Nivå ${info.level}, ${info.into} av ${info.needed} XP`)} style={{ textDecoration: 'none' }}>
      <LevelHex level={info.level} />
      <span className="xp-meta">
        <span className="xp-line"><span style={{ color: 'var(--text-2)' }}>{info.title.toUpperCase()}</span><span>{info.into}/{info.needed} XP</span></span>
        <Meter value={info.progress} thin seg label={L('XP progress', 'XP-fremgang')} />
      </span>
    </Link>
  );
}

function Notifications() {
  const [open, setOpen] = useState(false);
  const notes = useStudio((s) => s.notifications);
  const unread = notes.filter((n) => !n.read).length;
  return (
    <div style={{ position: 'relative' }}>
      <button className="icon-btn" aria-label={`${L('Notifications', 'Varsler')}${unread ? `, ${unread} ${L('unread', 'uleste')}` : ''}`} onClick={() => { setOpen((v) => !v); if (unread) store.dispatch({ type: 'NOTIFICATIONS_READ' }); }}>
        <Icon name="bell" />
        {unread > 0 && <span className="dot" />}
      </button>
      {open && (
        <div className="notif-panel plate hi" role="dialog" aria-label={L('Notifications', 'Varsler')}>
          <Sys code="LOG">{L('Notifications', 'Varsler')}</Sys>
          <div className="list mt">
            {notes.length === 0 && <p className="muted small">{L('Nothing yet.', 'Ingenting ennå.')}</p>}
            {[...notes].reverse().slice(0, 20).map((n) => (
              <button key={n.id} className="list-row notif" onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}>
                <span className={`notif-dot ${n.kind}`} />
                <span className="grow" style={{ textAlign: 'left' }}>
                  <div className="title small">{n.title}</div>
                  <div className="tiny muted">{n.body}</div>
                </span>
                <span className="tiny faint nowrap">{relTime(n.at)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function LangSwitch() {
  const lang = useStudio((s) => s.settings.language);
  return (
    <div className="lang-switch" role="group" aria-label={L('Language', 'Språk')}>
      {LANGS.map((l) => (
        <button key={l.id} className={lang === l.id ? 'on' : ''} aria-pressed={lang === l.id} title={l.label}
          onClick={() => store.dispatch({ type: 'SETTINGS_CHANGED', patch: { language: l.id as Lang } })}>{l.short}</button>
      ))}
    </div>
  );
}

function SoundToggle() {
  const on = useStudio((s) => s.settings.sound);
  return (
    <button className="icon-btn" aria-pressed={on} aria-label={on ? L('Mute sounds', 'Slå av lyd') : L('Turn sounds on', 'Slå på lyd')}
      onClick={() => store.dispatch({ type: 'SETTINGS_CHANGED', patch: { sound: !on } })}>
      <Icon name={on ? 'volume' : 'mute'} />
    </button>
  );
}

function Topbar({ onMenu, onSearch }: { onMenu: () => void; onSearch: () => void }) {
  const streak = useStudio((s) => s.streak.current);
  return (
    <header className="topbar">
      <button className="icon-btn only-mobile" onClick={onMenu} aria-label={L('Open navigation', 'Åpne meny')}><Icon name="menu" /></button>
      <button className="cmd-trigger" onClick={onSearch} aria-label={L('Search and commands', 'Søk og kommandoer')}>
        <Icon name="search" />
        <span>{L('Search skills, notes, commands…', 'Søk i ferdigheter, notater, kommandoer …')}</span>
        <kbd>Ctrl K</kbd>
      </button>
      <div className="top-stats">
        <span className={`streak hide-sm ${streak >= 3 ? 'hot' : ''}`} title={LL('Days in a row you learned something', 'Dager på rad du har lært noe', { detailed: ['Days in a row with real learning activity', 'Dager på rad med ekte læringsaktivitet'] })}><Icon name="flame" />{streak}</span>
        <span className="hide-sm"><XpWidget /></span>
        <span className="hide-sm"><LangSwitch /></span>
        <SoundToggle />
        <Notifications />
        <AccountButton />
      </div>
    </header>
  );
}

function commands() {
  return [
    { label: L('Go to Today', 'Gå til I dag'), to: '/today', keywords: 'home command center hjem' },
    { label: LL('Assessment (competence aims)', 'Vurdering (kompetansemål)', { detailed: ['Assessment against competence aims', 'Vurdering mot kompetansemål'] }), to: '/assess', keywords: 'grade kompetansemål vurdering karakter udir' },
    { label: LL('Start level check', 'Start nivåsjekk', { detailed: ['Start calibration', 'Start kartlegging'] }), to: '/calibration', keywords: 'placement diagnostic test calibrate kartlegging' },
    { label: L('Open knowledge map', 'Åpne kunnskapskartet'), to: '/map', keywords: 'graph skills network kart' },
    { label: L('Due reviews', 'Repetisjoner'), to: '/reviews', keywords: 'spaced repetition flashcards repetisjon' },
    { label: L('Mistake bank', 'Feilbank'), to: '/mistakes', keywords: 'errors recovery feil' },
    { label: L('Generate a course', 'Lag et kurs'), to: '/courses', keywords: 'learn plan course generator kurs' },
    { label: L('Start an exam session', 'Start en prøve'), to: '/exams', keywords: 'test mock exam prøve' },
    { label: L('New note', 'Nytt notat'), to: '/notes?new=1', keywords: 'write note notat' },
    { label: LL('Explain it (Feynman)', 'Forklar det (Feynman)', { detailed: ['Feynman mode', 'Feynman-modus'] }), to: '/feynman', keywords: 'explain teach forklar' },
    { label: L('Writing coach', 'Skrivecoach'), to: '/writing', keywords: 'essay text feedback skriving' },
    { label: L('Study chat (AI helper)', 'Studiechat (KI-hjelper)'), to: '/chat', keywords: 'chat ai tutor ask help question spør hjelp ki chatbot' },
    { label: L('Account, log in and sync', 'Konto, innlogging og synkronisering'), to: '/account', keywords: 'login log in sign up password sync account konto logg inn passord' },
    { label: L('Memory palace', 'Minnepalass'), to: '/palace', keywords: 'loci method minne' },
    { label: L('Planner', 'Planlegger'), to: '/planner', keywords: 'schedule calendar kalender' },
    { label: L('Learning profile', 'Læringsprofil'), to: '/profile', keywords: 'stage grade pathway education trinn' },
    { label: L('Settings', 'Innstillinger'), to: '/settings', keywords: 'motion effects theme language sound språk lyd' },
    { label: L('Sound design & research', 'Lyddesign og forskning'), to: '/research', keywords: 'psychology sound research psykologi lyd' },
  ];
}

function CommandPalette({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const state = useAppState();
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(() => {
    const cmd = commands().filter((c) => !q || `${c.label} ${c.keywords}`.toLowerCase().includes(q.toLowerCase())).slice(0, q ? 4 : 8).map((c) => ({ key: c.to, kind: L('command', 'kommando'), title: c.label, subtitle: '', route: c.to }));
    const hits = q.trim().length >= 2 ? searchCatalog(q, { state, limit: 14 }).map((h) => ({ key: `${h.kind}:${h.id}`, kind: h.kind, title: h.title, subtitle: h.subtitle, route: h.route })) : [];
    return [...cmd, ...hits];
  }, [q, state]);
  useEffect(() => inputRef.current?.focus(), []);
  useEffect(() => setSel(0), [q]);
  const go = (route: string) => {
    onClose();
    navigate(route);
  };
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal plate hi" role="dialog" aria-modal="true" aria-label={L('Search and commands', 'Søk og kommandoer')} style={{ padding: 0 }}>
        <div className="row" style={{ paddingLeft: 16 }}>
          <Icon name="search" size={18} />
          <input
            ref={inputRef} className="palette-input" placeholder={L('Search everything or type a command…', 'Søk i alt, eller skriv en kommando …')} value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(results.length - 1, s + 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
              if (e.key === 'Enter' && results[sel]) go(results[sel].route);
              if (e.key === 'Escape') onClose();
            }}
            aria-label={L('Search', 'Søk')}
          />
        </div>
        <div className="palette-list" role="listbox">
          {results.length === 0 && <p className="muted small" style={{ padding: 14 }}>{L('No matches.', 'Ingen treff.')}</p>}
          {results.map((r, i) => (
            <button key={r.key} role="option" aria-selected={i === sel} className={`palette-item ${i === sel ? 'sel' : ''}`} onMouseEnter={() => setSel(i)} onClick={() => go(r.route)}>
              <span className="pk">{r.kind}</span>
              <span className="grow">
                <div>{r.title}</div>
                {r.subtitle && <div className="tiny faint">{r.subtitle}</div>}
              </span>
              <Icon name="arrow-right" size={14} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface ToastItem { id: number; kind: string; k: string; title: string; body: string; leaving?: boolean }

/** Toasts, XP floats, sounds and celebrations — all driven by processed store events. */
function Effects() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [floats, setFloats] = useState<{ id: number; amount: number; x: number; y: number }[]>([]);
  const [flash, setFlash] = useState(0);
  const counter = useRef(0);
  const combo = useRef(0);
  const [comboN, setComboN] = useState(0);
  useEffect(() => {
    const push = (kind: string, k: string, title: string, body: string) => {
      const id = ++counter.current;
      setToasts((t) => [...t.slice(-3), { id, kind, k, title, body }]);
      setTimeout(() => setToasts((t) => t.map((x) => (x.id === id ? { ...x, leaving: true } : x))), 4800);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5200);
    };
    const offToast = onToast((t) => {
      push(t.kind, t.k ?? (t.kind === 'error' ? L('Notice', 'Melding') : L('System', 'System')), t.title, t.body ?? '');
      if (t.kind !== 'error') play('notify');
    });
    let lastFallback = 0;
    const offFallback = onAiFallback((err) => {
      if (Date.now() - lastFallback < 30_000) return;
      lastFallback = Date.now();
      push('error', L('AI', 'KI'), L('Claude did not answer — Studio used its own tools', 'Claude svarte ikke — Studio brukte sine egne verktøy'), err.message);
    });
    const off = store.on('*', (e: StudioEvent, s) => {
      switch (e.type) {
        case 'QUESTION_ANSWERED': {
          // Assessment answers never reveal correctness through sound.
          if (e.source === 'diagnostic' || e.source === 'exam') { play('neutral'); break; }
          if (e.evaluation.correct) {
            combo.current += 1;
            setComboN(combo.current);
            if (combo.current >= 3) play('combo', { combo: combo.current }); else play('correct');
            if (combo.current === 5 || combo.current === 10) burst({ count: 26, kind: 'stars' });
          } else {
            combo.current = 0;
            setComboN(0);
            play('wrong');
          }
          break;
        }
        case 'SKILL_MASTERED':
          systemNotify({ kind: 'mastery', title: L('Skill mastered', 'Ferdighet mestret'), lines: [catalog.skill(e.skillId)?.name ?? '', LL('You did it! Connected paths light up on your map.', 'Du klarte det! Stier som henger sammen lyser opp på kartet.', { easy: ['You did it! New paths open on your map.', 'Du klarte det! Nye stier åpner seg på kartet.'], detailed: ['Demonstrated at the mastery target. Connected paths brighten on the map.', 'Vist på mestringsnivået. Stier som henger sammen lyser opp på kartet.'] })] });
          setFlash((f) => f + 1);
          play('mastery');
          burst({ count: 60 });
          break;
        case 'SKILL_UNLOCKED':
          push('mastery', L('Path unlocked', 'Ny sti åpnet'), catalog.skill(e.skillId)?.name ?? '', LL('Unlocked! You have shown the skills it needs.', 'Låst opp! Du har vist det som trengs.', { detailed: ['Its prerequisites are now demonstrated.', 'Forkunnskapene er nå vist.'] }));
          play('sparkle');
          break;
        case 'PREREQUISITE_GAP_DETECTED':
          push('gap', LL('Missing piece', 'Manglende brikke', { detailed: ['Prerequisite gap', 'Hull i forkunnskaper'] }), catalog.skill(e.prerequisiteId)?.name ?? '', LL(`This blocks ${e.blocks.length} later skill(s). A practice quest was added to Today.`, `Dette stopper ${e.blocks.length} senere ferdighet(er). Et øvingsoppdrag er lagt til i I dag.`, { detailed: [`Blocks ${e.blocks.length} later skill(s). Recovery added to Today.`, `Stopper ${e.blocks.length} senere ferdighet(er). Øving er lagt til i I dag.`] }));
          break;
        case 'ACHIEVEMENT_UNLOCKED': {
          const n = s.notifications[s.notifications.length - 1];
          systemNotify({ kind: 'achievement', title: L('Achievement unlocked', 'Prestasjon låst opp'), lines: [n?.title ?? '', n?.body ?? ''].filter(Boolean) });
          play('achievement');
          burst({ count: 40, kind: 'stars' });
          break;
        }
        case 'QUEST_COMPLETED': {
          const q = s.quests.find((x) => x.id === e.questId);
          if (q) systemNotify({ kind: 'quest', title: L('Quest complete', 'Oppdrag fullført'), lines: [q.title], reward: `+${q.rewardXp} XP` });
          play('achievement');
          break;
        }
        case 'MISTAKE_RESOLVED':
          push('system', L('Mistake fixed', 'Feil rettet opp'), L('Mistake resolved', 'Feilen er løst'), LL('You passed the re-test. Nice comeback!', 'Du klarte testen igjen. Godt comeback!', { detailed: ['Re-test passed — evidence updated.', 'Ny test bestått — bevisene er oppdatert.'] }));
          play('sparkle');
          break;
        case 'BOSS_DEFEATED':
          systemNotify({ kind: 'boss', title: L('Boss defeated', 'Bossen er beseiret'), lines: [L('The gate is open.', 'Porten er åpen.'), LL('A big milestone — well fought!', 'En stor milepæl — godt kjempet!', { detailed: ['A major milestone.', 'En stor milepæl.'] })] });
          play('levelup');
          burst({ count: 90 });
          break;
        case 'LEVEL_UP':
          play('levelup');
          burst({ count: 80 });
          break;
        case 'XP_AWARDED':
          if (e.amount >= 2) {
            const id = ++counter.current;
            setFloats((f) => [...f.slice(-4), { id, amount: e.amount, x: window.innerWidth - 230 - Math.random() * 40, y: 30 }]);
            setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 1500);
          }
          break;
      }
    });
    return () => { off(); offToast(); offFallback(); };
  }, []);
  return (
    <>
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind} ${t.leaving ? 'leaving' : ''}`}>
            <div className="t-k">{t.k}</div>
            <div className="t-t">{t.title}</div>
            {t.body && <div className="t-b">{t.body}</div>}
          </div>
        ))}
      </div>
      {floats.map((f) => <div key={f.id} className="xp-float" style={{ left: f.x, top: f.y }}>+{f.amount} XP</div>)}
      <ComboPop combo={comboN} />
      {flash > 0 && <div key={flash} className="mastery-flash" />}
      <BurstLayer />
    </>
  );
}

/** Subtle tap sounds and a pressed ripple on buttons — UI acknowledges every action. */
function useTapFeedback() {
  const reduced = useReducedMotion();
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('.btn, .opt, .tile, .seg button, .nav-item, .kid-card, .token');
      if (!el || (el as HTMLButtonElement).disabled) return;
      play(el.matches('.opt, .tile, .seg button, .token') ? 'select' : 'tap');
      if (reduced || !el.matches('.btn')) return;
      const r = el.getBoundingClientRect();
      const size = Math.max(r.width, r.height);
      const span = document.createElement('span');
      span.className = 'ripple';
      span.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX - r.left - size / 2}px;top:${e.clientY - r.top - size / 2}px`;
      el.appendChild(span);
      setTimeout(() => span.remove(), 560);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [reduced]);
}

/**
 * Break reminders: after ~25 minutes of continuous activity Studio suggests a
 * short pause (spacing beats cramming, and attention recovers with breaks).
 * Studio never nags to keep a learner online.
 */
function useBreakReminder() {
  const enabled = useStudio((s) => s.settings.breakReminders);
  useEffect(() => {
    if (!enabled) return;
    let activeSince = Date.now();
    let lastInput = Date.now();
    let reminded = false;
    const mark = () => {
      const now = Date.now();
      if (now - lastInput > 5 * 60_000) { activeSince = now; reminded = false; } // a real break happened
      lastInput = now;
    };
    const id = setInterval(() => {
      if (!reminded && Date.now() - activeSince > 25 * 60_000 && Date.now() - lastInput < 60_000) {
        reminded = true;
        import('@/ui/toast').then(({ toast }) => toast(
          L('Time for a short break?', 'Tid for en liten pause?'),
          LL('You\'ve been at it for 25 minutes. A short break helps your brain save what you practised.', 'Du har holdt på i 25 minutter. En liten pause hjelper hjernen å lagre det du har øvd på.', { easy: ['Take a little break. Your brain needs rest too!', 'Ta en liten pause. Hjernen trenger også hvile!'], detailed: ['You have been learning for 25 minutes. A few minutes away helps your brain store what you practised.', 'Du har lært i 25 minutter. Noen minutter borte fra skjermen hjelper hjernen å lagre det du har øvd på.'] }),
        ));
      }
    }, 30_000);
    window.addEventListener('pointerdown', mark);
    window.addEventListener('keydown', mark);
    return () => {
      clearInterval(id);
      window.removeEventListener('pointerdown', mark);
      window.removeEventListener('keydown', mark);
    };
  }, [enabled]);
}

function LevelUpOverlay() {
  const [lv, setLv] = useState<{ from: number; to: number } | null>(null);
  const reduced = useReducedMotion();
  const total = useStudio((s) => s.xp.total);
  useEffect(() => {
    const off = store.on('LEVEL_UP', (e) => setLv({ from: e.from, to: e.to }));
    return () => { off(); };
  }, []);
  if (!lv) return null;
  const info = levelInfo(total);
  const unlocks = levelUnlocks().filter((u) => u.level > lv.from && u.level <= lv.to);
  return (
    <div className="levelup" role="dialog" aria-modal="true" aria-label={L(`Level up to ${lv.to}`, `Nytt nivå: ${lv.to}`)} onClick={() => setLv(null)}>
      <div className="burst" />
      {!reduced && Array.from({ length: 36 }, (_, i) => {
        const a = (i / 36) * Math.PI * 2;
        const r = 160 + Math.random() * 260;
        return <span key={i} className="ptl" style={{ left: '50%', top: '45%', ['--dx' as string]: `${Math.cos(a) * r}px`, ['--dy' as string]: `${Math.sin(a) * r}px`, animationDelay: `${Math.random() * 300}ms` } as CSSProperties} />;
      })}
      <div className="lu-core" onClick={(e) => e.stopPropagation()}>
        <div className="lu-k">{L('[ SYSTEM ] LEVEL UP', '[ SYSTEM ] NYTT NIVÅ')}</div>
        <div className="lu-num">{lv.to}</div>
        <div className="lu-title">{info.title}</div>
        <div className="lu-bar"><Meter value={info.progress} tall seg /></div>
        <div className="lu-lines">
          <TypeLines speed={20} lines={[
            L(`You reached level ${lv.to}!`, `Du nådde nivå ${lv.to}!`),
            L(`New title: ${info.title}.`, `Ny tittel: ${info.title}.`),
            L(`Next level in ${Math.max(0, info.needed - info.into)} XP.`, `Neste nivå om ${Math.max(0, info.needed - info.into)} XP.`),
          ]} />
        </div>
        <p className="muted small" style={{ maxWidth: 440 }}>{LL('You earn XP by learning new things: hard tasks, mastered skills, fixed mistakes and reviews. Repeating what you already know gives no XP.', 'Du får XP når du lærer noe nytt: vanskelige oppgaver, mestrede ferdigheter, rettede feil og repetisjon. Å gjenta det du allerede kan, gir ingen XP.', {
          easy: ['You get XP when you learn something new.', 'Du får XP når du lærer noe nytt.'],
          detailed: ['Earned through demonstrated learning: stretch tasks, mastery gains, recovered mistakes and reviews. XP is never given for repeating what you already know.', 'Opptjent gjennom vist læring: krevende oppgaver, mestring, rettede feil og repetisjon. Du får aldri XP for å gjenta det du allerede kan.'],
        })}</p>
        {unlocks.length > 0 && (
          <div className="lu-unlock plate hi" style={{ textAlign: 'left' }}>
            <Sys code="UNLOCK">{L('New power unlocked', 'Ny kraft låst opp')}</Sys>
            {unlocks.map((u) => (
              <div key={u.id} className="mt">
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 17 }}>{u.name}</div>
                <div className="small muted">{u.description}</div>
              </div>
            ))}
          </div>
        )}
        <button className="btn primary lg" onClick={() => setLv(null)}>{L('Continue', 'Fortsett')}</button>
      </div>
    </div>
  );
}

function MobileNav({ onMore }: { onMore: () => void }) {
  const route = useRoute();
  const items = [
    { to: '/today', label: L('Today', 'I dag'), icon: 'today' },
    { to: '/learn', label: L('Learn', 'Lær'), icon: 'learn' },
    { to: '/assess', label: L('Assess', 'Vurdering'), icon: 'assess' },
    { to: '/mastery', label: L('Progress', 'Fremgang'), icon: 'mastery' },
  ];
  return (
    <nav className="mobile-nav" aria-label={L('Quick navigation', 'Hurtigmeny')}>
      {items.map((it) => (
        <Link key={it.to} to={it.to} className={route.path.startsWith(it.to) ? 'active' : ''}>
          <Icon name={it.icon} />
          {it.label}
        </Link>
      ))}
      <button onClick={onMore}><Icon name="menu" />{L('More', 'Mer')}</button>
    </nav>
  );
}

/** Once a day: a "daily quests" system window greets the learner. */
function useDailyGreeting() {
  useEffect(() => {
    const today = new Date().toDateString();
    try {
      if (localStorage.getItem('studio.daily.v1') === today) return;
      localStorage.setItem('studio.daily.v1', today);
    } catch {
      return;
    }
    const s = store.getState();
    if (!s.profile) return;
    const now = Date.now();
    const name = s.profile.name;
    const fresh = now - s.profile.createdAt < 15 * 60_000;
    const open = s.quests.filter((q) => !q.completedAt && (!q.expiresAt || q.expiresAt > now)).length;
    const streak = s.streak.current;
    const lines = fresh
      ? [L(`Welcome, ${name}! Your adventure starts now.`, `Velkommen, ${name}! Eventyret ditt starter nå.`), L('Finish the level check to unlock your first quests.', 'Fullfør nivåsjekken for å låse opp de første oppdragene.')]
      : [
        L(`Welcome back, ${name}!`, `Velkommen tilbake, ${name}!`),
        open ? L(`${open} quest${open === 1 ? ' is' : 's are'} waiting for you.`, `${open} oppdrag venter på deg.`) : L('New quests appear as you learn.', 'Nye oppdrag dukker opp mens du lærer.'),
        streak >= 2 ? L(`Streak: ${streak} days in a row. Keep it going!`, `Rekke: ${streak} dager på rad. Hold den gående!`) : L('Learn something today to start a streak.', 'Lær noe i dag for å starte en rekke.'),
      ];
    const id = setTimeout(() => systemNotify({ kind: 'daily', title: fresh ? L('New adventure', 'Nytt eventyr') : L('Daily quests', 'Dagens oppdrag'), lines }), 900);
    return () => clearTimeout(id);
  }, []);
}

export function Shell({ children }: { children: ReactNode }) {
  const [menu, setMenu] = useState(false);
  const [palette, setPalette] = useState(false);
  const route = useRoute();
  useTapFeedback();
  useBreakReminder();
  useDailyGreeting();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette((v) => !v); }
      const t = e.target as HTMLElement;
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) { e.preventDefault(); setPalette(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="shell">
      <a href="#main" className="skip-link" onClick={(e) => { e.preventDefault(); document.getElementById('main')?.focus(); }}>{L('Skip to content', 'Hopp til innhold')}</a>
      <Rail open={menu} onClose={() => setMenu(false)} />
      <div className="main">
        <Topbar onMenu={() => setMenu(true)} onSearch={() => { setPalette(true); play('open'); }} />
        <main id="main" tabIndex={-1} style={{ outline: 'none' }}>
          <div key={`scan:${route.path}`} className="route-scan" aria-hidden="true" />
          {children}
        </main>
      </div>
      <MobileNav onMore={() => setMenu(true)} />
      {palette && <CommandPalette onClose={() => setPalette(false)} />}
      <Effects />
      <LevelUpOverlay />
    </div>
  );
}
